import { CharacterDialog } from '@/components/CharacterDialog';
import { CharacterFolders, useCharacterFolders } from '@/components/CharacterFolders';
import { useSearchParams } from 'react-router-dom';
import {
  type CharacterDto,
  type CharacterKind,
  STYLE_PRESETS,
} from '@studio/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Sparkles, Square, Trash2, UserRound } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ConfirmDialog, NativeSelect } from '@/components/ui/controls';
import { EmptyState, ErrorAlert, PageHeader, Skeleton } from '@/components/ui/misc';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { isActive, useCharacters } from '@/lib/queries';

const KIND_LABEL: Record<CharacterKind, string> = {
  organ: 'Cartoon organ',
  person: 'Person',
  mascot: 'Mascot',
  product: 'Product character',
};

/**
 * A full-body picture on a plain backdrop, drawn on the GPU from the description. It becomes picture 1: storyboard
 * stills place the character into the scene from it, so the same face and clothes show up in every shot.
 */
function CastPicture({ c }: { c: CharacterDto }) {
  const qc = useQueryClient();
  const { me } = useAuth();
  const [style, setStyle] = useState<string>(c.kind === 'person' ? STYLE_PRESETS[1] : STYLE_PRESETS[0]);
  const job = c.castJob;
  const busy = !!job && isActive(job.status);
  const done = (r: CharacterDto) => {
    qc.setQueryData<CharacterDto[]>(['characters'], (list) => list?.map((x) => (x.id === r.id ? r : x)));
    void qc.invalidateQueries({ queryKey: ['gpu'] });
  };
  const make = useMutation({
    mutationFn: () => api<CharacterDto>(`/api/characters/${c.id}/cast-picture`, { method: 'POST', body: { style } }),
    onSuccess: (r) => {
      done(r);
      toast.success('Drawing the cast picture on the GPU');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  const cancel = useMutation({
    mutationFn: () => api<CharacterDto>(`/api/characters/${c.id}/cast-picture/cancel`, { method: 'POST' }),
    onSuccess: done,
    onError: (e) => toast.error(errorMessage(e)),
  });
  if (busy)
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Loader2 className="size-3.5 animate-spin" /> {job.progressNote ?? 'Drawing the cast picture…'}
        <Button variant="ghost" size="sm" className="ml-auto h-7 px-2 text-xs" onClick={() => cancel.mutate()}>
          <Square /> Cancel
        </Button>
      </div>
    );
  const full = c.images.length >= 3;
  if (me && !me.imagesAvailable) return null;
  return (
    <div className="space-y-1">
      <div className="flex gap-1">
        <NativeSelect className="h-7 flex-1 text-xs" value={style} onChange={(e) => setStyle(e.target.value)}>
          {STYLE_PRESETS.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </NativeSelect>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 text-xs"
          onClick={() => make.mutate()}
          loading={make.isPending}
          disabled={full || !c.visualDescription.trim()}
          title={
            full
              ? 'This character has 3 pictures — remove one first'
              : !c.visualDescription.trim()
                ? 'Write what it looks like first'
                : 'Draw front, three-quarter and face pictures on the GPU'
          }
        >
          <Sparkles /> Make character sheet
        </Button>
      </div>
      {job?.status === 'failed' && <p className="text-xs text-destructive">Cast picture failed: {job.error}</p>}
    </div>
  );
}

export function CharactersPage() {
  const qc = useQueryClient();
  const q = useCharacters();
  const [params, setParams] = useSearchParams();
  const folders = useCharacterFolders();
  const folder = folders.data?.find((f) => f.id === params.get('folder'));
  const visible = q.data?.filter((c) => c.folderId === folder?.id);

  const [editing, setEditing] = useState<CharacterDto | null>(null);
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState<CharacterDto | null>(null);
  const del = useMutation({
    mutationFn: (c: CharacterDto) => api(`/api/characters/${c.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      void qc.invalidateQueries({ queryKey: ['characters'] });
    },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <PageHeader
        title={folder ? folder.title : "Characters"}
        description="People, cartoon organs, mascots — reusable in every project."
        actions={folder &&
          <Button
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus /> New character
          </Button>
        }
      />
      <CharacterFolders folderId={folder?.id ?? ''} onChange={(id) => setParams(id ? { folder: id } : {})} />
      {q.error && <ErrorAlert message={errorMessage(q.error)} />}
      {q.isLoading && <Skeleton className="h-48" />}
      {folder && visible && !visible.length && <EmptyState icon={<UserRound />} title="No characters yet" />}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {folder && visible?.map((c) => (
          <Card key={c.id} className="space-y-2 p-3">
            <div className="flex gap-3">
            <button
              type="button"
              className="flex min-w-0 flex-1 gap-3 text-left"
              onClick={() => {
                setEditing(c);
                setOpen(true);
              }}
            >
              <div className="grid h-28 w-20 shrink-0 place-items-center overflow-hidden rounded-md bg-muted">
                {c.images[0] ? (
                  <img src={c.images[0].url} alt="" className="size-full object-cover" />
                ) : (
                  <UserRound className="size-6 text-muted-foreground" />
                )}
              </div>
              <div className="min-w-0 space-y-1">
                <div className="font-medium">{c.name}</div>
                <div className="flex flex-wrap gap-1">
                  <Badge variant="secondary">{KIND_LABEL[c.kind]}</Badge>
                  <Badge variant="outline">
                    {c.images.length} picture{c.images.length === 1 ? '' : 's'}
                  </Badge>
                </div>
                <p className="line-clamp-3 text-xs text-muted-foreground">
                  {c.visualDescription || 'No description yet'}
                </p>
                {c.voiceDescription && <p className="line-clamp-1 text-xs">🔊 {c.voiceDescription}</p>}
                <div className="flex flex-wrap gap-1">
                  {c.outfitImage && <Badge variant="outline">👗 outfit set</Badge>}
                  {c.background && <Badge variant="secondary">🔒 {c.background.name}</Badge>}
                </div>
              </div>
            </button>
            <Button
              variant="ghost"
              size="icon"
              className="text-muted-foreground"
              onClick={() => setDeleting(c)}
              aria-label={`Delete ${c.name}`}
            >
              <Trash2 />
            </Button>
            </div>
            <CastPicture c={c} />
          </Card>
        ))}
      </div>
      {folder && <CharacterDialog character={editing} folderId={folder.id} open={open} onOpenChange={setOpen} />}
      <ConfirmDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title={`Delete ${deleting?.name}?`}
        description="Projects stop listing it; shots that mention it keep their text."
        confirmLabel="Delete"
        destructive
        loading={del.isPending}
        onConfirm={() => deleting && del.mutate(deleting)}
      />
    </div>
  );
}
